/*
 * `plan_name` raw format on AffiliateCommissionSchema and AffiliateAdminOrderRowSchema:
 *
 *     "NLHOLDEM - CASH - STARTER - MONTHLY - V1"
 *
 * Grammar:  GAME - FORMAT - PLAN - BILLING - VERSION
 *
 * The VERSION segment (V1/V2/…/bare "4") is dropped — it's an internal plan
 * revision marker, useless to humans reading commission rows.
 *
 * Defensive: missing or extra segments are tolerated; unknown tokens
 * fall back to a title-cased version of the raw token rather than crashing
 * the row.
 *
 * The raw technical string is preserved in a tooltip for support / debugging.
 *
 * Localization
 * ------------
 * `parsePlanName` returns the English label as a deterministic fallback
 * (also used directly by the unit tests). The React `<PlanName>` component
 * then resolves the user-visible chip text from the message catalog
 * (`plan.<kind>.<token>`), falling back to `segment.label` when the catalog
 * has no entry — so unmapped tokens still render, just untranslated.
 */

const GAME_TOKEN_LABEL: Record<string, string> = {
  NLHOLDEM: "NL Hold'em",
  PLO4: 'PLO',
}

const FORMAT_TOKEN_LABEL: Record<string, string> = {
  CASH: 'Cash',
  MTT: 'MTT',
  TOURNAMENT: 'Tournament',
  SPINS: 'Spin & Go',
  HUSNG: 'HU SNG',
  STRADDLE_ANTE_CASH: 'Straddle/Ante Cash',
  ANY: 'All formats',
}

// The closed set of subscription tiers. `TierToken` keys BOTH the label map and
// the pill-colour map below, so adding a tier without a colour (or vice versa)
// is a compile error — the type system guards the two maps against drift, no
// runtime check needed.
export type TierToken = 'STARTER' | 'PREMIUM' | 'ELITE' | 'ULTRA'

const TIER_TOKEN_LABEL: Record<TierToken, string> = {
  STARTER: 'Starter',
  PREMIUM: 'Premium',
  ELITE: 'Elite',
  ULTRA: 'Ultra',
}

const BILLING_TOKEN_LABEL: Record<string, string> = {
  MONTHLY: 'Monthly',
  YEARLY: 'Yearly',
}

const titleCase = (token: string): string =>
  token
    .split(/[\s_-]+/)
    .filter((s) => s.length > 0)
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase())
    .join(' ')

const isVersionToken = (token: string): boolean => {
  // V1, V2, V12, … or a bare numeric token like "4".
  return /^v\d+$/i.test(token) || /^\d+$/.test(token)
}

export type ParsedPlanSegment = {
  /** Original raw token (already trimmed and upper-cased). */
  raw: string
  /** Display label — mapped where known, title-cased fallback otherwise. */
  label: string
  /** Which grammar position this segment occupies, when we can identify it. */
  kind: 'game' | 'format' | 'tier' | 'billing' | 'unknown'
}

const lookup = (
  token: string,
  map: Record<string, string>,
  kind: ParsedPlanSegment['kind'],
): ParsedPlanSegment | null => {
  const label = map[token]
  if (label === undefined) {
    return null
  }

  return { raw: token, label, kind }
}

export const parsePlanName = (value: string | null | undefined): ParsedPlanSegment[] => {
  if (value == null) {
    return []
  }
  const tokens = value
    // Split on the documented ` - ` separator (space-dash-space), not a bare
    // `-`, so a future token with an internal hyphen (e.g. "HOLD-EM") isn't
    // over-split into spurious chips.
    .split(/\s+-\s+/)
    .map((s) => s.trim().toUpperCase())
    .filter((s) => s.length > 0)
    .filter((t) => !isVersionToken(t))

  return tokens.map((token): ParsedPlanSegment => {
    const game = lookup(token, GAME_TOKEN_LABEL, 'game')
    if (game !== null) {
      return game
    }
    const format = lookup(token, FORMAT_TOKEN_LABEL, 'format')
    if (format !== null) {
      return format
    }
    const tier = lookup(token, TIER_TOKEN_LABEL, 'tier')
    if (tier !== null) {
      return tier
    }
    const billing = lookup(token, BILLING_TOKEN_LABEL, 'billing')
    if (billing !== null) {
      return billing
    }

    return { raw: token, label: titleCase(token), kind: 'unknown' }
  })
}
